import { Download, KeyRound, LogOut, Plus, RefreshCw, Server, Settings } from 'lucide-react';
import { useState } from 'react';
import { Avatar, Popover, PopoverContent, PopoverTrigger, cn } from '@waymail/ui';
import { useContas, useSincronizar } from '../lib/consultas';
import { instalar, usePodeInstalar } from '../lib/instalar';
import { useUi } from '../store/ui';

/**
 * Menu do usuário, no canto superior direito: contas, sincronizar, gerenciar
 * e sair. Segue o segundo print de referência.
 */
export function MenuDoUsuario({
  onSair,
  onAdicionarConta,
}: {
  onSair: () => void;
  onAdicionarConta: () => void;
}) {
  const abrirConfiguracoes = useUi((s) => s.abrirConfiguracoes);
  // Controlado: as Configuracoes nao sao mais um modal que rouba o foco, e o
  // menu ficaria aberto por cima da tela que acabou de abrir.
  const [aberto, setAberto] = useState(false);
  const accountId = useUi((s) => s.accountId);
  const selectAccount = useUi((s) => s.selectAccount);

  const contas = useContas();
  const sincronizar = useSincronizar();
  const podeInstalar = usePodeInstalar();
  const ativa = contas.data?.find((c) => c.id === accountId) ?? contas.data?.[0];

  return (
    <div className="absolute top-4 right-6 z-20">
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Sua conta e configurações"
            className="cursor-pointer rounded-full ring-2 ring-transparent transition-shadow hover:ring-subtle"
          >
            <Avatar size="md" name={ativa?.email ?? '?'} src={ativa?.photo ?? undefined} />
          </button>
        </PopoverTrigger>

        <PopoverContent label="Sua conta" align="end" className="w-[320px] p-0">
          <div className="p-5">
            <p className="mb-4 text-[15px] font-bold text-primary">Perfil</p>
            <div className="flex items-center gap-3">
              <Avatar size="lg" name={ativa?.email ?? '?'} src={ativa?.photo ?? undefined} />
              <div className="min-w-0">
                <p className="truncate text-[14px] font-bold text-primary">
                  {ativa?.displayName ?? 'Sem caixa conectada'}
                </p>
                <p className="truncate text-[13px] text-tertiary">{ativa?.email ?? ''}</p>
              </div>
            </div>
          </div>

          <Secao titulo="Caixas">
            {contas.data?.map((conta) => (
              <button
                key={conta.id}
                type="button"
                onClick={() => selectAccount(conta.id)}
                // A caixa aberta nao pode depender so de cor para ser percebida.
                aria-current={conta.id === ativa?.id ? 'true' : undefined}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-hover',
                  conta.id === ativa?.id && 'bg-selected',
                )}
              >
                <Avatar size="xs" name={conta.email} src={conta.photo ?? undefined} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-primary">
                  {conta.email}
                </span>
                {conta.syncState === 'error' && (
                  <span className="shrink-0 text-[11px] font-semibold text-error">erro</span>
                )}
              </button>
            ))}

            <button
              type="button"
              onClick={onAdicionarConta}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-hover"
            >
              <Plus size={17} strokeWidth={1.8} aria-hidden="true" className="text-tertiary" />
              <span className="flex-1 text-[13px] font-medium text-primary">Adicionar caixa</span>
            </button>
          </Secao>

          <Secao titulo="Gerenciar">
            <ItemDoMenu
              icon={RefreshCw}
              onClick={() => {
                if (ativa !== undefined) sincronizar.mutate(ativa.id);
              }}
            >
              {sincronizar.isPending ? 'Sincronizando...' : 'Sincronizar agora'}
            </ItemDoMenu>
            <ItemDoMenu
              icon={Settings}
              onClick={() => {
                setAberto(false);
                abrirConfiguracoes();
              }}
            >
              Configurações
            </ItemDoMenu>
            <ItemDoMenu
              icon={Server}
              onClick={() => {
                setAberto(false);
                abrirConfiguracoes('hospedagem');
              }}
            >
              Hospedagem
            </ItemDoMenu>
            {podeInstalar && (
              <ItemDoMenu icon={Download} onClick={() => void instalar()}>
                Instalar o app
              </ItemDoMenu>
            )}
            <ItemDoMenu
              icon={KeyRound}
              onClick={() => {
                setAberto(false);
                abrirConfiguracoes('hospedagem');
              }}
            >
              Alterar senha (no painel)
            </ItemDoMenu>
          </Secao>

          <div className="border-t border-subtle p-4">
            <button
              type="button"
              onClick={onSair}
              className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-rail text-[14px] font-semibold text-white transition-colors hover:brightness-95"
            >
              <LogOut size={16} strokeWidth={2} aria-hidden="true" />
              Sair
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="px-3 pt-5">
      <p className="px-2 pb-1 text-[12px] font-semibold text-tertiary">{titulo}</p>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

function ItemDoMenu({
  icon: Icon,
  children,
  onClick,
}: {
  icon: typeof Settings;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      {...(onClick !== undefined ? { onClick } : {})}
      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left text-[13px] font-medium text-primary hover:bg-hover"
    >
      <Icon size={17} strokeWidth={1.8} aria-hidden="true" className="text-tertiary" />
      {children}
    </button>
  );
}
